import styled, { css } from 'styled-components/macro'
import { transparentize } from 'polished'
import { Href } from 'components/ui'

export const CloseButton = styled.button`
  position: absolute;
  top: 1.8rem;
  right: 2rem;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  color: ${(props) => props.theme.color.text};
  background-color: transparent;
  border: none;
  outline: none;
  cursor: pointer;
`

export const Wrap = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`

export const HeaderModal = styled.header`
  margin: 0;
  padding: 2.8rem 3.2rem;
  font-weight: ${(props) => props.theme.font.weight.medium};
  font-size: 2rem;
  line-height: 1.6;
  color: ${(props) => props.theme.color.white};
  text-align: center;
  border-bottom: 0.1rem solid ${(props) => transparentize(0.9, props.theme.color.white)};
  &::first-letter {
    text-transform: capitalize;
  }
  ${(props) => props.theme.breakpoints.greaterThan('sm')`
    padding: 1.2rem 3.2rem;
  `};
`

export const HeaderTitle = styled.h2`
  margin: 0;
  padding: 0;
  font-weight: ${(props) => props.theme.font.weight.medium};
  font-size: 2rem;
  line-height: 1.6;
  color: ${(props) => props.theme.color.white};
`

export const Container = styled.div`
  margin: 0;
`

export const WarningText = styled.p`
  margin: 0;
  padding: 0 0 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.4rem;
  font-weight: ${(props) => props.theme.font.weight.regular};
  font-size: 1.4rem;
  line-height: 1.43;
  text-align: center;
`

export const WrapContent = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.6rem;
  height: 38.2rem;
`

export const WrapContentApprove = styled.div`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  height: 31rem;
`

export const Wrapper = styled.div`
  margin: 0 0 6rem;
`

export const Text = styled.p<{ type?: string; size?: string; align?: string }>`
  margin: 0;
  padding: 0;
  font-weight: ${(props) => props.theme.font.weight.regular};
  font-size: 1.6rem;
  line-height: 1.12;
  text-align: ${(props) => (props.align ? props.align : 'center')};
  color: ${(props) => props.theme.color.text};
  &::first-letter {
    text-transform: capitalize;
  }
  ${(props) =>
    props.type === 'error' &&
    css`
      color: ${(props) => props.theme.color.danger};
    `}
  ${(props) =>
    props.type === 'support' &&
    css`
      color: ${(props) => transparentize(0.5, props.theme.color.text)};
    `}
  ${(props) =>
    props.size === 'small' &&
    css`
      font-size: 1.4rem;
      line-height: 1.12;
    `}
`

export const StyledLink = styled(Href)`
  padding: 0.4rem 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  text-align: center;
  color: ${(props) => props.theme.color.textHintSecond};
  border-radius: 0.8rem;
  &:hover {
    color: ${(props) => props.theme.color.text};
    background-color: ${(props) => props.theme.color.buttonBgThird};
  }
`

export const Section = styled.section`
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  background-color: ${(props) => props.theme.color.mediumBg};
  border-radius: 1.2rem;
`

export const InfoText = styled.p`
  margin: 0.8rem auto 2.4rem;
  max-width: 40rem;
  font-weight: ${(props) => props.theme.font.weight.bold};
  font-size: 1.6rem;
  line-height: 1.43;
  text-align: center;
  color: ${(props) => transparentize(0.2, props.theme.color.text)};
`

export const InfoTextSmall = styled.p`
  margin: 0;
  font-weight: ${(props) => props.theme.font.weight.regular};
  font-size: 1.3rem;
  line-height: 1.33;
  text-align: center;
  color: ${(props) => transparentize(0.5, props.theme.color.text)};
`

export const Steps = styled.div`
  position: relative;
  margin: 0;
  height: 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.2rem;
  ${(props) => props.theme.breakpoints.greaterThan('sm')`
    margin: 0 2rem;
  `};
`

export const StepLabel = styled.label<{ isActive: boolean; status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  font-weight: ${(props) => props.theme.font.weight.medium};
  font-size: 1.4rem;
  line-height: 1.33;
  color: ${(props) => transparentize(0.5, props.theme.color.text)};
  ${(props) =>
    props.isActive &&
    css`
      color: ${(props) => props.theme.color.default};
    `}
  ${(props) =>
    props.status === 'complete' &&
    css`
      color: ${(props) => props.theme.color.success};
    `}
  ${(props) =>
    props.status === 'failed' &&
    css`
      color: ${(props) => props.theme.color.danger};
    `}
`

export const StepStatus = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
`

export const StepInfo = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.color.elementBg};
  padding: 0 0.7rem;
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 1rem;
    height: 100%;
    background-color: ${(props) => props.theme.color.elementBg};
    z-index: ${(props) => props.theme.zIndex.minimal};
  }
  &::before {
    left: -1rem;
  }
  &::after {
    right: -1rem;
  }
`

export const StepsLine = styled.hr`
  all: initial;
  position: absolute;
  border-bottom: 0.1rem solid ${(props) => transparentize(0.5, props.theme.color.text)};
  width: 100%;
  height: 0;
  z-index: ${(props) => props.theme.zIndex.negative};
`

export const HeaderModalStepper = styled.div`
  margin: 0;
  padding: 0 0 2.4rem;
  font-size: 0;
  border-bottom: 0.1rem solid ${(props) => transparentize(0.9, props.theme.color.text)};
`

export const SkeletonWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.2rem;
`

export const Ring = styled.span`
  display: inline-block;
  width: 1.6rem;
  height: 1.6rem;
  border: 0.1rem solid;
  border-color: inherit;
  border-radius: 50%;
`
